import React, { useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { motion } from 'framer-motion';
import { Button } from 'components/Elements';
import LogoImg from 'assets/logo.png'
import { FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { ArrowRight } from 'react-feather';
import { Link } from 'react-router-dom';

const COLORS = ['#231F20', '#FFFFFF', '#8884d8', '#82ca9d', '#ffc658'];

const tokenAllocations = [
  { name: 'Community Grants', value: 40 },
  { name: 'DAO Treasury', value: 25 },
  { name: 'Team & Contributors', value: 15 },
  { name: 'Adoption & Growth', value: 10 },
  { name: 'Reserve', value: 10 },
];

export const Landing = () => {
  const tokenRef = useRef(null);
  const roadmapRef = useRef(null);
  const faqsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav className="bg-[#231F20] text-white px-6 py-4 flex justify-between items-center sticky top-0 z-50">
        <div className="flex flex-row items-center space-x-3">
          <img src={LogoImg} alt="Ashita Logo" className='h-8' />
          <h1 className="hidden md:block text-md font-bold">Ashita Creatives DAO</h1>
        </div>
        <div className="space-x-4 text-sm">
          <button onClick={() => scrollToSection(tokenRef)} className="hover:underline">Tokenomics</button>
          <button onClick={() => scrollToSection(roadmapRef)} className="hover:underline">Roadmap</button>
          <button onClick={() => scrollToSection(faqsRef)} className="hover:underline">FAQs</button>

          <a
            href="https://t.me/ashitacreativesDAO"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:underline"
          >
            <FaTelegram />
          </a>
          <a
            href="https://chat.whatsapp.com/KLyYODzz9xy10AVK1bgyn7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:underline"
          >
            <FaWhatsapp />
          </a>
        </div>
      </nav>

      <section className="bg-[#231F20] text-white min-h-screen py-32 px-6 flex items-center justify-center relative">

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto text-center"
        >
          <Link to="/get-started" className="mx-auto w-fit mb-4 flex flex-row items-center space-x-2 py-1 rounded-full px-6 pt-2 text-gray-600 hover:text-white border border-gray-800 bg-[#231F20]">
            <span>Welcome to Ashita Creatives DAO</span>
            <ArrowRight size={10} className='-mt-1' />
          </Link>
          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-6">
            <span className="relative inline-block">
              <span className="relative z-10 mr-4">Creative Skills</span>
              <span className={`absolute left-0 bottom-1 w-full h-3 bg-[url('assets/white-brush-stroke.png')] bg-no-repeat bg-contain bg-center z-0`}></span>
            </span>
            Can Build Great Cities!
          </h1>

          <p className="text-lg md:text-xl text-gray-300">
            Ashita Creatives DAO is weaving blockchain with creativity, allowing <span className="font-black">$ASH</span> token holders to shape dreams, fund vision, and spark collective magic. This living, breathing project carries the soul of local artistry into the digital ether — where stories rise, communities bloom, and every block becomes a brushstroke on the canvas of tomorrow.
          </p>
          <div className="mt-10 flex justify-center space-x-4">
            <a
              href="https://t.me/ashitacreativesDAO" // replace with your actual Telegram channel link
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="dark">Join the DAO</Button>
            </a>
            <a
              href="https://chat.whatsapp.com/B9tF2FPYWd8E5LB7GWeCE2" // replace with your actual Telegram channel link
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="dark">Submit Creative Project</Button>
            </a>

          </div>
        </motion.div>
      </section>

      <section ref={tokenRef} className="bg-white text-[#231F20] py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-semibold mb-4">$ASH Token</h2>
            <p className="text-lg mb-4">
              $ASH is the utility token of Ashita Creatives DAO, empowering creatives to vote, fund, and participate in meaningful art-driven impact.
            </p>
            <ul className="list-disc pl-6 text-sm space-y-2">
              <li>Total Supply: 1,000,000 $ASH</li>
              <li>Governance Voting</li>
              <li>Creative Grants & Bounties</li>
              <li>Project Funding & Collaborations</li>
              <li>Community Rewards</li>
            </ul>
          </motion.div>
          <motion.div
            className="w-full h-80"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={tokenAllocations}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {tokenAllocations.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>
        </div>
      </section>

      <section ref={roadmapRef} className="bg-[#F9F9F9] text-[#231F20] py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-semibold mb-12 text-center">Roadmap</h2>
          <div className="relative border-l-2 border-[#231F20] pl-6 space-y-10">
            {[
              { quarter: 'Q2 2025', text: 'DAO Formation, Safe Wallet Setup, Core Team Onboarded' },
              { quarter: 'Q3 2025', text: 'Token Launch, Community Onboarding, Snapshot Setup' },
              { quarter: 'Q4 2025', text: 'First Proposals Funded, Marketplace Integration (Zora, Rodeo), Training Workshops Begin' },
              { quarter: 'Q1 2026', text: 'Creative Grants Distribution, Community Projects in Uganda' },
              { quarter: 'Q2 2026', text: 'Launch of Ashita Project Protocol & P2P Smart Contract System' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="relative"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="absolute -left-3 top-1 w-4 h-4 bg-[#231F20] rounded-full"></div>
                <p><strong>{item.quarter}:</strong> {item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section ref={faqsRef} className="bg-white text-[#231F20] py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-semibold mb-10 text-center">FAQs</h2>
          <div className="space-y-4 text-sm">
            {[
              {
                q: 'What is Ashita Creatives DAO?',
                a: 'A decentralized community of creatives using art, poetry, music and design to earn, collaborate and uplift vulnerable communities.'
              },
              {
                q: 'How do I get $ASH tokens?',
                a: 'You can earn by participating in DAO projects, contributing to community initiatives, or joining our onboarding bounties.'
              },
              {
                q: 'What platforms are integrated?',
                a: 'We work with platforms like Zora, Rodeo (NFTs), Snapshot (governance), Farcaster (social), and Safe Wallet (treasury).'
              },
              {
                q: 'Can I join if I’m new to Web3?',
                a: 'Absolutely! We’re onboarding creatives from all walks of life. We provide education and peer mentorship to help you succeed.'
              }
            ].map((faq, i) => (
              <details key={i} className="border border-[#231F20] rounded p-4">
                <summary className="font-semibold cursor-pointer">{faq.q}</summary>
                <p className="mt-2 text-gray-700">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#231F20] text-white py-10 px-6">
        <div className="max-w-6xl mx-auto text-center space-y-2 text-sm">
          <p>&copy; {new Date().getFullYear()} Ashita Creatives DAO. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
};